
import { matchPath, useLocation } from "react-router-dom";

import { Feature } from "../../../../common/models/Feature";
import { RoutePath } from "../../../../common/RoutePath";
import { config } from "../../../../config/frontend";
import analyticsCustomPropertiesURL from "../../../assets/img/imgAnalyticsCustomProperties.png";
import analyticsCustomProperties2xURL from "../../../assets/img/imgAnalyticsCustomProperties@2x.png";
import analyticsCustomProperties3xURL from "../../../assets/img/imgAnalyticsCustomProperties@3x.png";
import componentsCustomPropertiesURL from "../../../assets/img/imgComponentsCustomProperties.png";
import componentsCustomProperties2xURL from "../../../assets/img/imgComponentsCustomProperties@2x.png";
import componentsCustomProperties3xURL from "../../../assets/img/imgComponentsCustomProperties@3x.png";
import { Button, ButtonAnchor, ButtonKind } from "../../../library/Button/Button";
import { Dialog } from "../../../library/Dialog/Dialog";
import { H2 } from "../../../library/Heading/Heading";
import { IconLock } from "../../../library/icons/IconLock";
import { IconMetadata } from "../../../library/icons/IconMetadata";
import { subscriptionPlanToString } from "../../../models/SubscriptionPlan";
import { isFeatureEnabled } from "../../../models/Workspace";
import { useStore } from "../../../providers/StoreProvider/StoreProvider";
import { trackContactUsLinkClick } from "../../../tracking";

import classes from "./CustomPropertiesDialog.module.css";

interface Props {
    onClose(): void;
}

export function CustomPropertiesDialog({ onClose }: Props) {
    const location = useLocation();
    const {
        actions: { openPricingTableDialog },
        selectors: { getWorkspace },
    } = useStore();
    const workspace = getWorkspace()!;
    const isCustomPropertiesEnabled = isFeatureEnabled(workspace, Feature.CustomProperties);

    const [customPropertiesURL, customProperties2xURL, customProperties3xURL] = matchPath(RoutePath.Components, location.pathname) !== null
        ? [componentsCustomPropertiesURL, componentsCustomProperties2xURL, componentsCustomProperties3xURL]
        : [analyticsCustomPropertiesURL, analyticsCustomProperties2xURL, analyticsCustomProperties3xURL];

    const icon = isCustomPropertiesEnabled ? <IconMetadata/> : <IconLock/>;
    const suggestion = isCustomPropertiesEnabled
        ? "See our docs to learn more about CLI hooks and how to add custom properties."
        : `You’re currently on the ${subscriptionPlanToString(workspace.subscription.plan)} plan where Custom Properties are not available. If you’d like to upgrade, check our plans below and let us know if you have any questions.`;

    function handlePricingClick() {
        onClose();
        openPricingTableDialog("custom_properties_dialog");
    }

    function handleContactUsClick() {
        trackContactUsLinkClick({ source: "custom_properties_dialog" });
    }

    return (
        <Dialog
            className={classes.customPropertiesDialog}
            onClose={onClose}>
            <div className={classes.content}>
                <div className={classes.header}>
                    {icon}
                    <H2 className={classes.h2}>
                        Custom properties
                    </H2>
                    <p className={classes.p}>
                        In addition to Omlet’s built-in properties, you can assign custom properties to your components
                        using Omlet’s CLI hooks. These custom properties can then be used to filter, tag and analyze components,
                        giving you more control over how to measure your design system.
                    </p>
                </div>
                <img
                    src={customPropertiesURL}
                    srcSet={`${customProperties2xURL} 2x, ${customProperties3xURL} 3x`}
                    alt="Filter components by custom properties"/>
                <div className={classes.footer}>
                    <p className={classes.p}>{suggestion}</p>
                    <div className={classes.buttons}>
                        {isCustomPropertiesEnabled
                            ? <ButtonAnchor href="/l/docs/cli/hooks" target="_blank">See docs</ButtonAnchor>
                            : <Button onClick={handlePricingClick}>See plans</Button>
                        }
                        {!isCustomPropertiesEnabled && (
                            <ButtonAnchor
                                kind={ButtonKind.Secondary}
                                href={`${config.LANDING_PAGE_BASE_URL}/contact/`}
                                target="_blank"
                                onClick={handleContactUsClick}>
                                Contact us
                            </ButtonAnchor>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
